import { type HeadToHeadOutcomes, type ResultRound, discardCountOf, rankByValue, totalOfRounds } from "./ranking";
import { ROUND_WORDS, type RoundWord, type RoundsMeetConfig, type RoundsResultDetail, type RoundsScoring } from "./results";
import { isSprintMatch } from "./timed-rules";
import type { IrmCode, MeetOutcome, PhaseType, ResultFamily } from "./vocab";

/**
 * ROUNDS — each sport's rules for a multi-round result (archery ends,
 * shooting series, golf rounds, sailing races, bowling games, the omnium's
 * races, BMX motos, equestrian rounds, water-ski passes). ONE place, shared by
 * OVR (the live totals, discards and ranks an operator sees while typing),
 * the TSR mappers and the results feed.
 *
 * Ported from the old OVR (`WSL-OVR-X meets/point/*`), with its gaps fixed
 * (docs/ovr-port/spec-rounds.md): golf ties share a rank (a play-off is a
 * typed rank), a sailing medal race counts double and is never discarded,
 * shooting ties go to a countback, archery set play stops at the winning set
 * point and the shoot-off decides — with closest-to-centre when level.
 *
 * Numbers marked CONFIRM are the old OVR's values or the federation's
 * standard as read; the technical delegates confirm them (NOTES-p5.md).
 * Everything here is pure.
 */

// ─── Which sport ─────────────────────────────────────────────────────────────

export const ROUNDS_SPORTS = [
	"ARCHERY",
	"SHOOTING",
	"GOLF",
	"SAILING",
	"BOWLING",
	"OMNIUM",
	"BMX_RACING",
	"JUMPING",
	"EVENTING",
	"SLALOM",
	"TRICKS",
	"OTHER",
] as const;
export type RoundsSport = (typeof ROUNDS_SPORTS)[number];

/** What a meet is, as far as its rules go: codes only, never names. */
export interface RoundsMeetRef {
	sportCode: string | null | undefined;
	disciplineCode?: string | null | undefined;
	eventCode?: string | null | undefined;
	/** A team event (golf / equestrian best-N). */
	team?: boolean;
	/** The event's largest team (`events.team_size_max`): a bowling team's game is its bowlers' sum. */
	teamSizeMax?: number | null;
	config?: RoundsMeetConfig | Record<string, unknown> | null | undefined;
}

const up = (s: string | null | undefined): string => (s ?? "").trim().toUpperCase();
const SPORT_ALIASES: Readonly<Record<string, string>> = { AR: "ARC", SH: "SHO" };
const baseSport = (code: string | null | undefined): string => {
	const s = up(code).split("-")[0] ?? "";
	return SPORT_ALIASES[s] ?? s;
};

/** The sport's rule set, by `sport.code`, the discipline's code and the event's code (seed: ARC/REC, CYC/BMX, EQU/JMP, WSK …SLAL). */
export function roundsSportOf(ref: Pick<RoundsMeetRef, "sportCode" | "disciplineCode" | "eventCode">): RoundsSport {
	const d = up(ref.disciplineCode);
	const e = up(ref.eventCode);
	switch (baseSport(ref.sportCode)) {
		case "ARC":
			return "ARCHERY";
		case "SHO":
			return "SHOOTING";
		case "GLF":
			return "GOLF";
		case "SAL":
			return "SAILING";
		case "BOW":
			return "BOWLING";
		case "CYC":
			if (d === "BMX") return "BMX_RACING";
			if (e.endsWith("OMN")) return "OMNIUM";
			return "OTHER";
		case "EQU":
			if (d === "JMP") return "JUMPING";
			if (d === "EVT") return "EVENTING";
			return "OTHER";
		case "WSK":
			if (e.includes("SLAL")) return "SLALOM";
			if (e.includes("TRICK")) return "TRICKS";
			return "OTHER";
		default:
			return "OTHER";
	}
}

// ─── The rules ───────────────────────────────────────────────────────────────

export const ROUND_PART_WORDS = ["arrow", "shot", "hole", "frame"] as const;
export type RoundPartWord = (typeof ROUND_PART_WORDS)[number];

/** The tallies a sport types beside its rounds (tie-breaks and sheet counts). Codes are the TSR mappers'. */
export const ROUNDS_TALLY_CODES = {
	/** Archery: 10s including X. */
	tens: "tens",
	/** Archery: Xs (inner 10s). */
	xCount: "x_count",
	/** Archery head-to-head: the shoot-off arrows' total. */
	shootOff: "shoot_off",
	/** Archery head-to-head: 1 = this archer's shoot-off arrow was closest to the centre. */
	soClosest: "so_closest",
	/** Archery set play: set points (as the TSR saves them). */
	setPoints: "set_points",
	/** Shooting: inner tens (X). */
	innerTens: "inner_tens",
	/** Shooting final: shots fired before elimination. */
	shotsFired: "shots_fired",
	/** Omnium: the place in the points race's final sprint (it breaks a tie). */
	finalSprintPlace: "final_sprint_place",
	/** Sailing: the total before discards. */
	gross: "gross",
} as const;

export interface RoundsRules {
	sport: RoundsSport;
	/** What a round is called (`config.round_word` wins). */
	word: RoundWord;
	/** How many round columns the sheet has, when the sport knows (`config.rounds` wins). */
	rounds: number | null;
	/** The parts inside a round (arrows of an end, holes of a round, shots of a series, frames of a game). */
	parts: { word: RoundPartWord; count: number } | null;
	/** Archery head-to-head scoring. */
	scoring: RoundsScoring | null;
	/** Tallies typed as columns, in order. */
	tallies: readonly string[];
	/** The default label of round `seq` (index seq − 1), when the sheet names its rounds. */
	labels: readonly string[];
	/** Decimals a round value is typed with. */
	dec: number;
	/** An IRM on a round fills the value with the code's points (sailing: entrants + 1). */
	codePoints: boolean;
	/** Rounds whose value is points FROM a typed place (the omnium's first three races). */
	placePointsRounds: readonly number[];
	/** The total adds each round's `points` (bowling Masters' match bonus). */
	addPoints: boolean;
	/** The course's par per hole (golf); null when not set. */
	par: readonly number[] | null;
	/** A team counts its best N members per round. */
	bestOf: number | null;
	/** The last round is a medal race (counts double, never discarded). */
	medalRace: boolean;
	/** Show to-par and thru (golf). */
	toPar: boolean;
	/**
	 * Regulation ends of a head-to-head (archery): `config.rounds`, else 5
	 * individual / 4 team (World Archery). A bowling Masters match: its games
	 * (`config.rounds`, else 3 on games won, 1 — the stepladder — on pins).
	 */
	matchEnds: number | null;
	/**
	 * Golf rounds, sailing races, bowling squads held as SEPARATE meets of one
	 * phase add up to one standing (the feed's series standings). Never across
	 * phases, never for other sports (a shooting final starts from zero).
	 */
	seriesAcrossMeets: boolean;
	/**
	 * A round's value is a whole number from 0 to this (bowling: 300 a game,
	 * × the team's size for a team row); null = any value to `dec`.
	 */
	roundMax: number | null;
	/**
	 * The parts are typed and the round is their sum (arrows, holes, shots).
	 * False for bowling: a frame's score carries the next balls' bonus, so
	 * frames are TSR's (ball by ball) and only shown; the game is typed.
	 */
	partsTyped: boolean;
	/** The config the totals use: the sport's discard steps filled in. */
	config: RoundsMeetConfig;
}

/**
 * Sailing discards by races sailed — the old OVR's rule (`sailingDiscardCount`):
 * from 7 races drop 1, from 11 races drop 2. CONFIRM with the regatta's
 * sailing instructions (they set it; RRS A2.1 only says "as the SIs state").
 */
export const SAILING_DISCARD_STEPS: readonly { from: number; discards: number }[] = [
	{ from: 7, discards: 1 },
	{ from: 11, discards: 2 },
];

/** World Archery: ends in a head-to-head — 5 individual (3 arrows), 4 team (6 arrows; mixed team 4). */
export const ARCHERY_MATCH_ENDS = { individual: 5, team: 4 } as const;
/** Shooting 50 m 3 positions: kneeling, prone, standing — two series each (the old OVR's K / P / S keys). */
export const SHOOTING_3P_LABELS = ["K1", "K2", "P1", "P2", "S1", "S2"] as const;
/** Eventing: dressage, cross-country, jumping. */
export const EVENTING_LABELS = ["DR", "XC", "SJ"] as const;
/** Jumping: two rounds and a jump-off (the jump-off is a decider). */
export const JUMPING_LABELS = ["R1", "R2", "JO"] as const;
/** Omnium (UCI 3.2.246): scratch, tempo, elimination, points race. */
export const OMNIUM_LABELS = ["SR", "TR", "ER", "PR"] as const;

const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const intIn = (v: unknown, lo: number, hi: number): number | null => (isNum(v) && Number.isInteger(v) && v >= lo && v <= hi ? v : null);

function readConfig(raw: RoundsMeetRef["config"]): RoundsMeetConfig {
	const c = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
	const out: RoundsMeetConfig = {};
	const rounds = intIn(c.rounds, 1, 40);
	if (rounds !== null) out.rounds = rounds;
	if (isNum(c.discards) && c.discards >= 0) out.discards = c.discards;
	if (Array.isArray(c.discard_steps)) {
		out.discard_steps = c.discard_steps.flatMap((s) => {
			const o = s as Record<string, unknown>;
			const from = intIn(o?.from, 1, 40);
			const discards = intIn(o?.discards, 0, 10);
			return from !== null && discards !== null ? [{ from, discards }] : [];
		});
	}
	if (typeof c.medal_race === "boolean") out.medal_race = c.medal_race;
	if (typeof c.round_word === "string" && (ROUND_WORDS as readonly string[]).includes(c.round_word)) out.round_word = c.round_word as RoundWord;
	if (c.scoring === "SET" || c.scoring === "CUMULATIVE") out.scoring = c.scoring;
	if (Array.isArray(c.par) && c.par.every((p) => intIn(p, 1, 9) !== null)) out.par = c.par as number[];
	const bestOf = intIn(c.best_of, 1, 10);
	if (bestOf !== null) out.best_of = bestOf;
	const dec = intIn(c.dec, 0, 3);
	if (dec !== null) out.dec = dec;
	return out;
}

/**
 * The rules of one meet. Defaults by sport; the meet's `config` overrides the
 * word, the round count, the scoring, the par, best-N and the decimals.
 *
 *   ARCHERY     end; arrows (≤ 6); SET for recurve (discipline REC), CUMULATIVE
 *               for compound (CMP); tallies 10+X, X (World Archery Book 3: ties
 *               in a ranking round go to most 10s, then most Xs)
 *   SHOOTING    series (shotgun: round); 10 shots (shotgun 25 targets); inner
 *               tens; 10 m rifle typed to 0.1 (the old OVR: rifle 10 m and
 *               50 m prone); 3 positions labelled K1 K2 P1 P2 S1 S2
 *   GOLF        round; 4 rounds of 18 holes; to-par from `config.par`; team
 *               best 3 (old OVR "best3")
 *   SAILING     race; IRMs score entrants + 1 (RRS A5.2); discards by
 *               SAILING_DISCARD_STEPS unless set; medal race doubled, kept
 *   BOWLING     game; 6 games of 10 frames (shown, never typed — TSR's);
 *               a game is 0–300 (× the team's size for a team row); a
 *               Masters round robin adds each game's bonus (`points`); a
 *               Masters match is won on pins (CUMULATIVE, the stepladder's
 *               one game) or on games won (SET, best of `config.rounds` ?? 3)
 *   OMNIUM      race; 4 races (SR TR ER PR); a place in the first three is
 *               worth `omniumPlacePoints`; the points race adds its points
 *   BMX_RACING  moto; 3 motos; the value is the place, lowest total wins
 *   JUMPING     round; R1 R2 and the jump-off JO (a decider)
 *   EVENTING    round; DR XC SJ, penalties to 0.1
 *   SLALOM      pass; buoys to 0.25 (2 decimals); the label is speed / line
 *   TRICKS      pass; 2 passes summed
 */
export function roundsRulesFor(ref: RoundsMeetRef): RoundsRules {
	const sport = roundsSportOf(ref);
	const config = readConfig(ref.config);
	const d = up(ref.disciplineCode);
	const e = up(ref.eventCode);
	const base: Omit<RoundsRules, "config"> = {
		sport,
		word: "round",
		rounds: null,
		parts: null,
		scoring: null,
		tallies: [],
		labels: [],
		dec: 0,
		codePoints: false,
		placePointsRounds: [],
		addPoints: false,
		par: null,
		bestOf: null,
		medalRace: config.medal_race === true,
		toPar: false,
		matchEnds: null,
		seriesAcrossMeets: sport === "GOLF" || sport === "SAILING" || sport === "BOWLING",
		roundMax: null,
		partsTyped: true,
	};
	switch (sport) {
		case "ARCHERY":
			Object.assign(base, {
				word: "end",
				parts: { word: "arrow", count: 6 },
				scoring: config.scoring ?? (d === "CMP" ? "CUMULATIVE" : "SET"),
				tallies: [ROUNDS_TALLY_CODES.tens, ROUNDS_TALLY_CODES.xCount],
				// Team and mixed team play 4 ends (first to 5 set points), individuals 5 (first to 6).
				matchEnds: config.rounds ?? (ref.team ? ARCHERY_MATCH_ENDS.team : ARCHERY_MATCH_ENDS.individual),
			});
			break;
		case "SHOOTING": {
			const shotgun = d === "SHG";
			Object.assign(base, {
				word: shotgun ? "round" : "series",
				parts: { word: "shot", count: shotgun ? 25 : 10 },
				tallies: shotgun ? [] : [ROUNDS_TALLY_CODES.innerTens],
				dec: d === "RIF" && !e.includes("3P") ? 1 : 0,
				labels: e.includes("3P") ? SHOOTING_3P_LABELS : [],
				rounds: e.includes("3P") ? 6 : null,
			});
			break;
		}
		case "GOLF":
			Object.assign(base, {
				word: "round",
				rounds: 4,
				parts: { word: "hole", count: 18 },
				toPar: true,
				bestOf: ref.team ? 3 : null,
			});
			break;
		case "SAILING":
			Object.assign(base, { word: "race", codePoints: true });
			break;
		case "BOWLING": {
			const masters = e.includes("MAST");
			const scoring: RoundsScoring | null = masters ? (config.scoring ?? "CUMULATIVE") : null;
			Object.assign(base, {
				word: "game",
				rounds: 6,
				parts: { word: "frame", count: BOWLING_FRAMES },
				addPoints: masters,
				scoring,
				// A match's games: best of 3 on games won, one stepladder game on pins (`config.rounds` either way).
				matchEnds: scoring === "SET" ? (config.rounds ?? BOWLING_MATCH_GAMES) : scoring === "CUMULATIVE" ? (config.rounds ?? 1) : null,
				roundMax: bowlingGameMax(ref.team === true ? ref.teamSizeMax : 1),
				partsTyped: false,
			});
			break;
		}
		case "OMNIUM":
			Object.assign(base, { word: "race", rounds: 4, labels: OMNIUM_LABELS, placePointsRounds: [1, 2, 3], tallies: [ROUNDS_TALLY_CODES.finalSprintPlace] });
			break;
		case "BMX_RACING":
			Object.assign(base, { word: "moto", rounds: 3 });
			break;
		case "JUMPING":
			Object.assign(base, { word: "round", rounds: 3, labels: JUMPING_LABELS, bestOf: ref.team ? 3 : null });
			break;
		case "EVENTING":
			Object.assign(base, { word: "round", rounds: 3, labels: EVENTING_LABELS, dec: 1, bestOf: ref.team ? 3 : null });
			break;
		case "SLALOM":
			Object.assign(base, { word: "pass", dec: 2 });
			break;
		case "TRICKS":
			Object.assign(base, { word: "pass", rounds: 2 });
			break;
		case "OTHER":
			break;
	}
	if (config.round_word) base.word = config.round_word;
	if (config.rounds !== undefined) base.rounds = config.rounds;
	if (config.dec !== undefined) base.dec = config.dec;
	if (config.par) base.par = config.par;
	if (config.best_of !== undefined) base.bestOf = config.best_of;
	const effective: RoundsMeetConfig = { ...config };
	if (sport === "SAILING" && effective.discards === undefined && effective.discard_steps === undefined) effective.discard_steps = [...SAILING_DISCARD_STEPS];
	return { ...base, config: effective };
}

/** The label round `seq` shows: its own, else the sport's default, else null (the screen writes word + seq). */
export function roundLabelOf(rules: Pick<RoundsRules, "labels" | "medalRace" | "rounds">, round: Pick<ResultRound, "seq" | "label"> | { seq: number; label?: string }): string | null {
	if (round.label) return round.label;
	if (rules.medalRace && rules.rounds !== null && round.seq === rules.rounds) return "MR";
	return rules.labels[round.seq - 1] ?? null;
}

/**
 * What a round carries because of WHERE it sits, written with its value: the
 * medal race doubled and kept, the jump-off a decider, the default label.
 * The detail is then self-describing — the feed and RS read it unchanged.
 */
export function roundFlagsFor(rules: RoundsRules, seq: number): Pick<ResultRound, "factor" | "keep" | "decider" | "label"> {
	const out: Pick<ResultRound, "factor" | "keep" | "decider" | "label"> = {};
	const label = roundLabelOf(rules, { seq });
	if (label) out.label = label;
	if (rules.medalRace && rules.rounds !== null && seq === rules.rounds) {
		out.factor = 2;
		out.keep = true;
		out.label = "MR";
	}
	if (rules.sport === "JUMPING" && label === "JO") out.decider = true;
	return out;
}

// ─── Totals ──────────────────────────────────────────────────────────────────

const tallyOf = (detail: { tallies?: RoundsResultDetail["tallies"] | null } | null | undefined, code: string): number | null => {
	const t = (detail?.tallies ?? []).find((x) => x?.code === code);
	return t && isNum(t.value) ? t.value : null;
};

/**
 * A row's total with its sport's rules: `totalOfRounds` over the effective
 * config (factor, keep, deciders, discards), plus each counted round's
 * `points` when the sport adds them (bowling Masters).
 */
export function roundsTotal(rules: RoundsRules, rounds: readonly ResultRound[] | null | undefined, lowerIsBetter: boolean): { total: number | null; discardedSeqs: number[] } {
	const t = totalOfRounds(rounds, rules.config, lowerIsBetter);
	if (!rules.addPoints || t.total === null) return t;
	const dropped = new Set(t.discardedSeqs);
	const bonus = (rounds ?? []).filter((r) => !dropped.has(r.seq) && r.decider !== true && isNum(r.points)).reduce((s, r) => s + (r.points as number), 0);
	return { total: Number((t.total + bonus).toFixed(4)), discardedSeqs: t.discardedSeqs };
}

// ─── Tie-breaks (smaller wins, as `rankByValue` compares them) ──────────────

const byseq = (rounds: readonly ResultRound[] | null | undefined) => [...(rounds ?? [])].filter((r) => isNum(r.value)).sort((a, b) => a.seq - b.seq);
/** "More is better" as "smaller wins": a missing count is worse than any. */
const more = (v: number | null): number => (v === null ? Number.POSITIVE_INFINITY : -v);

/** Archery ranking round (World Archery Book 3, 14.4 — CONFIRM): most 10s (X included), then most Xs. */
export function archeryTieBreak(detail: Pick<RoundsResultDetail, "tallies"> | null | undefined): number[] {
	return [more(tallyOf(detail, ROUNDS_TALLY_CODES.tens)), more(tallyOf(detail, ROUNDS_TALLY_CODES.xCount))];
}

/**
 * Shooting qualification (ISSF countback — CONFIRM, the old OVR had only X):
 * most inner tens, then the last series, the one before it, … back to the first.
 */
export function shootingTieBreak(detail: Pick<RoundsResultDetail, "tallies" | "rounds"> | null | undefined): number[] {
	const series = byseq(detail?.rounds).filter((r) => r.decider !== true);
	return [more(tallyOf(detail, ROUNDS_TALLY_CODES.innerTens)), ...series.reverse().map((r) => -r.value)];
}

/**
 * Sailing (World Sailing RRS A8, the medal race first): the medal race score;
 * then A8.1 — the scores that count, best first (excluded scores not used);
 * then A8.2 — the last race, the one before it, … (excluded scores used).
 */
export function sailingTieBreak(rounds: readonly ResultRound[] | null | undefined, discardedSeqs: readonly number[]): number[] {
	const list = byseq(rounds).filter((r) => r.decider !== true);
	const medal = list.filter((r) => r.keep === true || r.factor === 2);
	const dropped = new Set(discardedSeqs);
	const counted = list.filter((r) => !dropped.has(r.seq)).map((r) => r.value).sort((a, b) => a - b);
	return [...medal.map((r) => r.value), ...counted, ...[...list].reverse().map((r) => r.value)];
}

/** Bowling (World Bowling — CONFIRM, the old OVR had none): the higher high game. */
export function bowlingTieBreak(rounds: readonly ResultRound[] | null | undefined): number[] {
	const games = byseq(rounds).map((r) => r.value);
	return [games.length ? -Math.max(...games) : Number.POSITIVE_INFINITY];
}

/** Equestrian jumping: the jump-off's faults, then its time. */
export function jumpingTieBreak(rounds: readonly ResultRound[] | null | undefined): number[] {
	const jo = byseq(rounds).find((r) => r.decider === true || r.label === "JO");
	if (!jo) return [];
	return [jo.value, jo.time_ms ?? Number.POSITIVE_INFINITY];
}

/** Eventing (FEI — CONFIRM): the better cross-country score (fewest XC penalties). */
export function eventingTieBreak(rounds: readonly ResultRound[] | null | undefined): number[] {
	const xc = byseq(rounds).find((r) => r.label === "XC") ?? byseq(rounds).find((r) => r.seq === 2);
	return xc ? [xc.value] : [];
}

/** BMX racing (UCI): the better place in the last moto. */
export function bmxTieBreak(rounds: readonly ResultRound[] | null | undefined): number[] {
	const last = byseq(rounds).at(-1);
	return last ? [last.value] : [];
}

/** Omnium (UCI 3.2.253): the better place in the points race's final sprint. */
export function omniumTieBreak(detail: Pick<RoundsResultDetail, "tallies"> | null | undefined): number[] {
	const place = tallyOf(detail, ROUNDS_TALLY_CODES.finalSprintPlace);
	return [place ?? Number.POSITIVE_INFINITY];
}

/** The sport's tie-break vector for one row (smaller wins). Golf, slalom and tricks have none: ties share, a play-off / run-off is a typed rank. */
export function roundsTieBreak(rules: RoundsRules, detail: RoundsResultDetail | null | undefined, discardedSeqs: readonly number[] = []): number[] {
	switch (rules.sport) {
		case "ARCHERY":
			return archeryTieBreak(detail);
		case "SHOOTING":
			return shootingTieBreak(detail);
		case "SAILING":
			return sailingTieBreak(detail?.rounds, discardedSeqs);
		case "BOWLING":
			return bowlingTieBreak(detail?.rounds);
		case "JUMPING":
			return jumpingTieBreak(detail?.rounds);
		case "EVENTING":
			return eventingTieBreak(detail?.rounds);
		case "BMX_RACING":
			return bmxTieBreak(detail?.rounds);
		case "OMNIUM":
			return omniumTieBreak(detail);
		default:
			return [];
	}
}

// ─── Ranks ───────────────────────────────────────────────────────────────────

export interface RoundsRankRow<Id> {
	id: Id;
	value: number | null;
	irm: IrmCode | null;
	detail: RoundsResultDetail | Record<string, unknown> | null | undefined;
	/** The stored (or drafted) rank — kept as is when the detail says `rank_locked`. */
	rank: number | null;
}

/**
 * The meet's ranks: by value in the meet's direction, ties broken by the
 * sport's countback (`roundsTieBreak`), still-equal rows sharing a rank.
 * A row whose detail says `rank_locked` keeps its typed rank — a shoot-off, a
 * play-off, a decision — and a later edit of any row never wipes it.
 */
export function roundsRanks<Id>(rows: readonly RoundsRankRow<Id>[], rules: RoundsRules, lowerIsBetter: boolean): Map<Id, number | null> {
	const ranks = rankByValue(
		rows.map((r) => {
			const detail = (r.detail ?? {}) as RoundsResultDetail;
			const discarded = totalOfRounds(detail.rounds, rules.config, lowerIsBetter).discardedSeqs;
			return { id: r.id, value: r.value, irm: r.irm, tieBreak: roundsTieBreak(rules, detail, discarded) };
		}),
		lowerIsBetter,
	);
	for (const r of rows) {
		if ((r.detail as RoundsResultDetail | null | undefined)?.rank_locked === true) ranks.set(r.id, r.irm === null ? r.rank : null);
	}
	return ranks;
}

// ─── Archery head-to-head ────────────────────────────────────────────────────

/** Set points per end (World Archery Book 3, 14.2.2): the higher end 2, level 1 each, the lower 0. Null until both have shot it. */
export function archeryEndPoints(a: number | null | undefined, b: number | null | undefined): [number, number] | null {
	if (!isNum(a) || !isNum(b)) return null;
	return a > b ? [2, 0] : a < b ? [0, 2] : [1, 1];
}

export interface ArcherySide {
	rounds: readonly ResultRound[] | null | undefined;
	tallies?: RoundsResultDetail["tallies"] | null;
	irm: IrmCode | null;
}

export type ArcheryDecidedBy = "IRM" | "SETS" | "TOTAL" | "SHOOT_OFF" | "CLOSEST";

export interface ArcheryVerdict {
	/** Each side's `result_value`: set points (SET) or the regulation arrow total (CUMULATIVE). */
	score: [number, number];
	/** Regulation arrow totals. */
	totals: [number, number];
	/** Per regulation end, the set points each side took (typed `points` win over the derived). Empty for CUMULATIVE. */
	ends: { seq: number; points: [number, number] }[];
	/** The shoot-off arrows' totals (`tallies.shoot_off`). */
	shootOff: [number | null, number | null];
	/** Who was closest to the centre (`tallies.so_closest` = 1). */
	closest: 0 | 1 | null;
	outcomes: HeadToHeadOutcomes | null;
	decidedBy: ArcheryDecidedBy | null;
}

const ended = (r: ResultRound | undefined): r is ResultRound => r !== undefined && isNum(r.value) && r.decider !== true;

/**
 * An archery match (World Archery Book 3, 14.2 — the old OVR's
 * `getEndPoints` / `getSOSumRaw`, its gaps fixed):
 *
 *   SET          ends paired by seq; 2 / 1 / 0 per end (a typed `points` on a
 *                round wins for that side); the match stops once a side reaches
 *                `ends + 1` set points (6 individual, 5 team: a 7–3 stands) —
 *                later ends are ignored; level
 *                after every end → the shoot-off: the higher arrow wins +1, a
 *                level shoot-off goes to the archer closest to the centre (+1).
 *   CUMULATIVE   the arrow total over the regulation ends; level → the
 *                shoot-off arrows, then closest to the centre. The shoot-off
 *                never adds to the total.
 *
 * One IRM → the other side wins. Outcomes stay null while undecided.
 * `ends` = regulation ends (`config.rounds`, default 5).
 */
export function archeryMatchVerdict(a: ArcherySide, b: ArcherySide, scoring: RoundsScoring, ends: number = ARCHERY_MATCH_ENDS.individual): ArcheryVerdict {
	const ra = new Map(byseq(a.rounds).filter((r) => r.decider !== true).map((r) => [r.seq, r]));
	const rb = new Map(byseq(b.rounds).filter((r) => r.decider !== true).map((r) => [r.seq, r]));
	const seqs = [...new Set([...ra.keys(), ...rb.keys()])].sort((x, y) => x - y).filter((s) => s <= ends);
	const totals: [number, number] = [0, 0];
	for (const s of seqs) {
		totals[0] += ra.get(s)?.value ?? 0;
		totals[1] += rb.get(s)?.value ?? 0;
	}
	const shootOff: [number | null, number | null] = [tallyOf(a, ROUNDS_TALLY_CODES.shootOff), tallyOf(b, ROUNDS_TALLY_CODES.shootOff)];
	const closest: 0 | 1 | null = tallyOf(a, ROUNDS_TALLY_CODES.soClosest) === 1 ? 0 : tallyOf(b, ROUNDS_TALLY_CODES.soClosest) === 1 ? 1 : null;
	const out: ArcheryVerdict = { score: [0, 0], totals, ends: [], shootOff, closest, outcomes: null, decidedBy: null };
	const win = (side: 0 | 1, by: ArcheryDecidedBy) => {
		out.outcomes = side === 0 ? ["WIN", "LOSS"] : ["LOSS", "WIN"];
		out.decidedBy = by;
	};
	/** The shoot-off: the higher arrow, then closest to the centre. */
	const shootOffWinner = (): { side: 0 | 1; by: ArcheryDecidedBy } | null => {
		const [x, y] = shootOff;
		if (isNum(x) && isNum(y) && x !== y) return { side: x > y ? 0 : 1, by: "SHOOT_OFF" };
		if (closest !== null) return { side: closest, by: "CLOSEST" };
		return null;
	};

	if (scoring === "SET") {
		const target = ends + 1;
		let complete = 0;
		for (const s of seqs) {
			const x = ra.get(s);
			const y = rb.get(s);
			if (!ended(x) || !ended(y)) continue;
			if (out.score[0] >= target || out.score[1] >= target) break;
			const derived = archeryEndPoints(x.value, y.value) as [number, number];
			const pts: [number, number] = [isNum(x.points) ? x.points : derived[0], isNum(y.points) ? y.points : derived[1]];
			out.ends.push({ seq: s, points: pts });
			out.score[0] += pts[0];
			out.score[1] += pts[1];
			complete++;
		}
		if (out.score[0] >= target !== out.score[1] >= target) {
			win(out.score[0] >= target ? 0 : 1, "SETS");
		} else if (complete >= ends && out.score[0] === out.score[1]) {
			const so = shootOffWinner();
			if (so) {
				out.score[so.side] += 1;
				win(so.side, so.by);
			}
		} else if (complete >= ends && out.score[0] !== out.score[1]) {
			// Every end shot and no one at the target (typed points): the higher wins.
			win(out.score[0] > out.score[1] ? 0 : 1, "SETS");
		}
	} else {
		out.score = [totals[0], totals[1]];
		const complete = seqs.filter((s) => ended(ra.get(s)) && ended(rb.get(s))).length;
		if (complete >= ends) {
			if (totals[0] !== totals[1]) win(totals[0] > totals[1] ? 0 : 1, "TOTAL");
			else {
				const so = shootOffWinner();
				if (so) win(so.side, so.by);
			}
		}
	}
	// An invalid result decides whatever the arrows say.
	if (a.irm !== null || b.irm !== null) {
		out.outcomes = null;
		out.decidedBy = null;
		if ((a.irm === null) !== (b.irm === null)) win(a.irm === null ? 0 : 1, "IRM");
	}
	return out;
}

/** "6–5 (S-O 10*–10)" style board text for one side: its score, and the shoot-off when one was shot. */
export function archeryDisplay(v: ArcheryVerdict, side: 0 | 1): string {
	const so = v.shootOff[side];
	if (so === null) return String(v.score[side]);
	return `${v.score[side]} (S-O ${so}${v.closest === side ? "*" : ""})`;
}

// ─── Sailing ─────────────────────────────────────────────────────────────────

/** The codes RRS A5.2 scores as entrants + 1. Others (SCP, ZFP, DPI, RDG, STP, DCT) are typed by the race committee. */
export const SAILING_ENTRANTS_PLUS_ONE: readonly IrmCode[] = ["DNC", "DNS", "OCS", "NSC", "DNF", "RET", "DSQ", "DNE", "UFD", "BFD"];

/** A race's points for a scoring code: entrants + 1 (RRS A5.2), else null (typed). */
export function sailingCodePoints(irm: IrmCode, entrants: number): number | null {
	return SAILING_ENTRANTS_PLUS_ONE.includes(irm) ? entrants + 1 : null;
}

/** Discards for a series of `racesSailed` races, by steps (default SAILING_DISCARD_STEPS). */
export function sailingDiscards(racesSailed: number, steps: readonly { from: number; discards: number }[] = SAILING_DISCARD_STEPS): number {
	return discardCountOf({ discard_steps: [...steps] }, racesSailed) ?? 0;
}

// ─── Golf ────────────────────────────────────────────────────────────────────

/**
 * Score to par over the holes played: Σ strokes − Σ par of those holes. A
 * round with no holes but a value counts as the full card. Null without par.
 */
export function golfToPar(rounds: readonly ResultRound[] | null | undefined, par: readonly number[] | null | undefined): number | null {
	if (!par || par.length === 0) return null;
	const card = par.reduce((s, p) => s + p, 0);
	let out: number | null = null;
	for (const r of byseq(rounds)) {
		if (r.decider === true) continue;
		const holes = (r.parts ?? []).filter((p) => isNum(p.value) && p.seq >= 1 && p.seq <= par.length);
		const diff = holes.length > 0 ? holes.reduce((s, p) => s + p.value - (par[p.seq - 1] as number), 0) : r.value - card;
		out = (out ?? 0) + diff;
	}
	return out;
}

/** Holes played in the latest round: "F" when finished (all holes, or a round with no hole detail), null with no round. */
export function golfThru(rounds: readonly ResultRound[] | null | undefined, holes = 18): number | "F" | null {
	const last = byseq(rounds).filter((r) => r.decider !== true).at(-1);
	if (!last) return null;
	const played = (last.parts ?? []).filter((p) => isNum(p.value)).length;
	return played === 0 || played >= holes ? "F" : played;
}

/** "E", "+3", "-2" — as a leaderboard prints it. */
export function formatToPar(n: number | null): string {
	if (n === null) return "";
	return n === 0 ? "E" : n > 0 ? `+${n}` : `-${Math.abs(n)}`;
}

/**
 * A team's rounds from its members' (golf best 3 of 4, equestrian 3 of 4):
 * per seq the sum of the best `n` valid member rounds (a round with an IRM —
 * CUT, DNS, WD — never counts); fewer than `n` valid → all of them (the old
 * OVR's rule). `dropped` = the member rounds left out, to flag `discarded`.
 */
export function teamBestOf(
	members: readonly (readonly ResultRound[] | null | undefined)[],
	n: number,
	lowerIsBetter: boolean,
): { rounds: ResultRound[]; dropped: { member: number; seq: number }[] } {
	const bySeq = new Map<number, { member: number; value: number }[]>();
	members.forEach((rounds, member) => {
		for (const r of byseq(rounds)) {
			if (r.irm !== undefined) continue;
			const list = bySeq.get(r.seq) ?? [];
			list.push({ member, value: r.value });
			bySeq.set(r.seq, list);
		}
	});
	const rounds: ResultRound[] = [];
	const dropped: { member: number; seq: number }[] = [];
	for (const seq of [...bySeq.keys()].sort((x, y) => x - y)) {
		const list = (bySeq.get(seq) ?? []).sort((x, y) => (lowerIsBetter ? x.value - y.value : y.value - x.value) || x.member - y.member);
		const kept = list.slice(0, Math.max(n, 0) || list.length);
		for (const x of list.slice(kept.length)) dropped.push({ member: x.member, seq });
		rounds.push({ seq, value: Number(kept.reduce((s, x) => s + x.value, 0).toFixed(4)) });
	}
	return { rounds, dropped };
}

// ─── Bowling ─────────────────────────────────────────────────────────────────

/** World Bowling Playing Rules 2.2: a game is ten frames; 300 is the perfect game. */
export const BOWLING_FRAMES = 10;
export const BOWLING_GAME_MAX = 300;
/** One frame's own score, bonus balls included (a strike followed by two strikes). */
export const BOWLING_FRAME_MAX = 30;
/** A Masters match on games won is best of 3 (the old OVR's rule); `config.rounds` changes it. */
export const BOWLING_MATCH_GAMES = 3;
/**
 * Masters round-robin bonus pins per game: a win, a tie, a loss (World
 * Bowling Masters — CONFIRM with the technical delegate). Typed per game as
 * the round's `points`; the total adds them (`roundsTotal`, `addPoints`).
 */
export const BOWLING_MASTERS_BONUS = { WIN: 10, DRAW: 5, LOSS: 0 } as const;
export const BOWLING_BONUS_VALUES: readonly number[] = [BOWLING_MASTERS_BONUS.LOSS, BOWLING_MASTERS_BONUS.DRAW, BOWLING_MASTERS_BONUS.WIN];

/** The highest game a row can hold: 300, × the team's size for a team row (its bowlers' sum). */
export function bowlingGameMax(teamSize: number | null | undefined): number {
	const n = isNum(teamSize) && Number.isInteger(teamSize) && teamSize >= 1 ? teamSize : 1;
	return BOWLING_GAME_MAX * n;
}

/**
 * A bowling team's games from its bowlers' (Doubles, Trios, Team): game N =
 * the sum of every bowler's game N (a game with an IRM never counts). Every
 * bowler counts — bowling has no best-N.
 */
export function bowlingTeamRounds(members: readonly (readonly ResultRound[] | null | undefined)[]): ResultRound[] {
	return teamBestOf(members, 0, false).rounds;
}

/**
 * What the API refuses in a bowling detail (a slot's or a bowler's line-up
 * row), as `path: message` lines: a game that is not a whole number from 0 to
 * `gameMax`, a frame beyond the tenth or outside 0–30, a bonus that is not
 * 0 / 5 / 10. Empty = fine. The schema checked the shape already.
 */
export function bowlingDetailIssues(detail: { rounds?: readonly ResultRound[] | null } | null | undefined, gameMax: number): string[] {
	const out: string[] = [];
	const whole = (v: unknown, max: number) => isNum(v) && Number.isInteger(v) && v >= 0 && v <= max;
	(detail?.rounds ?? []).forEach((r, i) => {
		if (!whole(r.value, gameMax)) out.push(`rounds.${i}.value: A game is a whole number from 0 to ${gameMax}`);
		(r.parts ?? []).forEach((p, j) => {
			if (p.seq > BOWLING_FRAMES) out.push(`rounds.${i}.parts.${j}.seq: A game has ${BOWLING_FRAMES} frames`);
			else if (!whole(p.value, BOWLING_FRAME_MAX * (gameMax / BOWLING_GAME_MAX))) out.push(`rounds.${i}.parts.${j}.value: A frame is a whole number from 0 to ${BOWLING_FRAME_MAX}`);
		});
		if (r.points !== undefined && !BOWLING_BONUS_VALUES.includes(r.points)) out.push(`rounds.${i}.points: The bonus is ${BOWLING_BONUS_VALUES.join(", ")}`);
	});
	return out;
}

export interface BowlingSide {
	rounds: readonly ResultRound[] | null | undefined;
	irm: IrmCode | null;
}

export interface BowlingVerdict {
	/** What the board shows: games won (SET) or pins (CUMULATIVE). */
	score: [number, number];
	/** Pins over the match's games. */
	pins: [number, number];
	/** Each game both bowled: its winner (null = level). */
	games: { seq: number; winner: 0 | 1 | null }[];
	outcomes: [MeetOutcome, MeetOutcome] | null;
}

/**
 * A Masters match (two bowlers, a knock-out phase), over its first `games`
 * games:
 *
 *   CUMULATIVE  the pins once both have bowled every game (a stepladder game
 *               is one game); level → no outcome: a roll-off (World Bowling
 *               3.5), picked by hand.
 *   SET         games won: first to a majority of `games` (2 of 3) wins;
 *               later games are ignored; every game bowled and level → the
 *               roll-off, picked by hand. A level game is won by no one.
 *
 * One IRM → the other side wins. Outcomes stay null while undecided.
 */
export function bowlingMatchVerdict(a: BowlingSide, b: BowlingSide, scoring: RoundsScoring, games: number): BowlingVerdict {
	const bowled = (r: ResultRound | undefined): r is ResultRound => r !== undefined && isNum(r.value) && r.irm === undefined;
	const ra = new Map(byseq(a.rounds).map((r) => [r.seq, r]));
	const rb = new Map(byseq(b.rounds).map((r) => [r.seq, r]));
	const out: BowlingVerdict = { score: [0, 0], pins: [0, 0], games: [], outcomes: null };
	const win = (side: 0 | 1) => {
		out.outcomes = side === 0 ? ["WIN", "LOSS"] : ["LOSS", "WIN"];
	};
	const target = Math.floor(games / 2) + 1;
	for (let s = 1; s <= games; s++) {
		const x = ra.get(s);
		const y = rb.get(s);
		out.pins[0] += x?.value ?? 0;
		out.pins[1] += y?.value ?? 0;
		if (!bowled(x) || !bowled(y)) continue;
		if (scoring === "SET" && (out.score[0] >= target || out.score[1] >= target)) continue;
		const winner: 0 | 1 | null = x.value === y.value ? null : x.value > y.value ? 0 : 1;
		out.games.push({ seq: s, winner });
		if (scoring === "SET" && winner !== null) out.score[winner] += 1;
	}
	if (scoring === "SET") {
		if (out.score[0] >= target !== out.score[1] >= target) win(out.score[0] >= target ? 0 : 1);
		else if (out.games.length >= games && out.score[0] !== out.score[1]) win(out.score[0] > out.score[1] ? 0 : 1);
	} else {
		out.score = [out.pins[0], out.pins[1]];
		if (out.games.length >= games && out.pins[0] !== out.pins[1]) win(out.pins[0] > out.pins[1] ? 0 : 1);
	}
	if (a.irm !== null || b.irm !== null) {
		out.outcomes = null;
		if ((a.irm === null) !== (b.irm === null)) win(a.irm === null ? 0 : 1);
	}
	return out;
}

/** Games bowled, pinfall, average (2 dp, as the old OVR's `toFixed(2)`) and the high game. */
export function bowlingSummary(rounds: readonly ResultRound[] | null | undefined): { games: number; total: number; average: number | null; high: number | null } {
	const games = byseq(rounds).map((r) => r.value);
	const total = games.reduce((s, v) => s + v, 0);
	return { games: games.length, total, average: games.length ? Number((total / games.length).toFixed(2)) : null, high: games.length ? Math.max(...games) : null };
}

// ─── Omnium ──────────────────────────────────────────────────────────────────

/** Omnium points for a place in the scratch, tempo or elimination race (UCI 3.2.253 — CONFIRM): 40, 38, 36 … 2 for 20th, then 1. */
export function omniumPlacePoints(place: number): number {
	if (!Number.isInteger(place) || place < 1) return 0;
	return place <= 20 ? 42 - 2 * place : 1;
}

// ─── Which units are matches ─────────────────────────────────────────────────

/** The knock-out phase types a head-to-head match is played in. A ranking round, heats, groups and rounds never are. */
export const MATCH_PHASE_TYPES: readonly PhaseType[] = ["R128", "R64", "R32", "R16", "QUARTERFINAL", "SEMIFINAL", "REPECHAGE", "BRONZE", "FINAL"];

export interface MatchUnitRef {
	family: ResultFamily;
	sportCode: string | null | undefined;
	disciplineCode?: string | null | undefined;
	eventCode?: string | null | undefined;
	phaseType: PhaseType | null | undefined;
	/** Slots in the meet. */
	slots: number;
}

/**
 * Is this meet a head-to-head MATCH by its sport's rules — decided from the
 * rules, never from the data (two slots and a stored outcome)? A two-boat
 * sailing race, a two-swimmer final or a two-golfer meet stays ranked; a live
 * archery match is a match before it has a winner. The OVR screens and the
 * results feed both ask this.
 *
 *   ROUNDS  archery (set play or cumulative) and a bowling Masters
 *           stepladder game, two slots, in a knock-out phase
 *   TIMED   a cycling sprint match (`isSprintMatch`)
 *   other   never (SETS / BOUT events are head-to-head as events)
 */
export function isMatchUnit(ref: MatchUnitRef): boolean {
	if (ref.slots !== 2 || !ref.phaseType || !MATCH_PHASE_TYPES.includes(ref.phaseType)) return false;
	if (ref.family === "ROUNDS") {
		const sport = roundsSportOf(ref);
		return sport === "ARCHERY" || (sport === "BOWLING" && up(ref.eventCode).includes("MAST"));
	}
	if (ref.family === "TIMED") return isSprintMatch(ref);
	return false;
}
